import { useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useTour } from '@reactour/tour'
import { useCADPageStore } from '@/state'
import { CameraPlusIcons } from '@/components/icons/CameraPlusIcon'
import { useTourProgressStore } from './state'
import { useLocation } from 'wouter'

const SELF_ONBOARDING_COMPLETED_KEY = 'self-onboarding-completed'

const TOUR_STEPS = {
  CAD: {
    selector: '[data-testid="cad-page-canvas"]',
    content: () => (
      <TourStepWrapper>
        <>
          <p className="mb-2">
            Use the mouse scroll wheel to zoom in and out of CAD.
          </p>
          <p>Use the right mouse button to pan.</p>
        </>
      </TourStepWrapper>
    ),
  },
  ADD_OPERATION: {
    selector: '[data-testid="add-button"]',
    content: () => (
      <TourStepWrapper>
        <p>Add an operation step</p>
      </TourStepWrapper>
    ),
  },
  ADD_PARTS: {
    selector: '[data-testid="step-1-card-drop-zone"]',
    highlightedSelectors: [
      '[data-testid="step-1-card-drop-zone"]',
      '[data-testid="assembly-tree"]',
    ],
    content: () => (
      <TourStepWrapper>
        <p>Add parts to your step</p>
      </TourStepWrapper>
    ),
  },
  TAKE_SCREENSHOT: {
    selector: '[data-testid="create-view-button"]',
    content: () => (
      <TourStepWrapper>
        <>
          <p className="mb-2 text-gray-800 leading-relaxed">
            Click
            <span className="inline align-middle text-primary-500 bg-primary-50 rounded-full mx-1 px-3 pt-0.5 pb-1">
              <CameraPlusIcons className="h-5 w-5 inline stroke-white" />
            </span>
            to take a live screenshot.
          </p>
          <p>These update as your assembly updates.</p>
        </>
      </TourStepWrapper>
    ),
  },
  UPDATE_CAD: {
    selector: '[data-testid="update-cad-version-button"]',
    content: () => (
      <TourStepWrapper>
        <p>Update your CAD and watch your document auto-update - like magic!</p>
      </TourStepWrapper>
    ),
  },
  NAVIGATE_TO_DOCUMENT: (navigate?: () => void) => ({
    selector: '[data-testid="document-page-button"]',
    action: () => navigate?.(),
    content: () => (
      <TourStepWrapper>
        <p>Your pages will autofill with the parts and screenshots.</p>
      </TourStepWrapper>
    ),
  }),
  NAVIGATE_TO_OPERATION: {
    selector: '[data-testid="operation-1"]',
    content: () => (
      <TourStepWrapper>
        <p>Navigate to an operation page.</p>
      </TourStepWrapper>
    ),
  },
  PUBLISH_CHANGES: {
    selector: '[data-testid="create-document-version-button"]',
    content: () => (
      <TourStepWrapper>
        <p>Version control to manage your documents.</p>
      </TourStepWrapper>
    ),
  },
}
export const AppTour = () => {
  const { setIsOpen, setSteps } = useTour()
  const cadLoaded = useCADPageStore(useShallow((state) => state.cadLoaded))
  const [location, setLocation] = useLocation()
  const { setTotalSteps, setInteractiveSteps } = useTourProgressStore(
    useShallow((state) => ({
      setTotalSteps: state.setTotalSteps,
      setInteractiveSteps: state.setInteractiveSteps,
    })),
  )

  useEffect(() => {
    setInteractiveSteps([1, 2, 3, 6])
  }, [setInteractiveSteps])

  useEffect(() => {
    const tourSteps = [
      TOUR_STEPS.CAD,
      TOUR_STEPS.ADD_OPERATION,
      TOUR_STEPS.ADD_PARTS,
      TOUR_STEPS.TAKE_SCREENSHOT,
      TOUR_STEPS.UPDATE_CAD,
      TOUR_STEPS.NAVIGATE_TO_DOCUMENT(() => {
        const updatedLocation = location.replace('cad', 'edit')
        setLocation(updatedLocation)
      }),
      TOUR_STEPS.NAVIGATE_TO_OPERATION,
      TOUR_STEPS.PUBLISH_CHANGES,
    ]

    if (setSteps) setSteps(tourSteps)
    setTotalSteps(tourSteps.length)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const tourCompleted = localStorage.getItem(SELF_ONBOARDING_COMPLETED_KEY)
    if (!cadLoaded || tourCompleted) return
    setTimeout(() => setIsOpen(true))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cadLoaded])

  return null
}

const TourStepWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-col p-2">{children}</div>
)
